function normalizeHostname(hostname: string): string {
    return hostname.toLowerCase().replace(/^www\./, '');
}

/**
 * Same registrable host policy as legacy core login: `www.example.com` matches `example.com`,
 * but host must still match the current page (protocol + port + normalized hostname).
 */
function isSameSiteForPostLoginRedirect(resolved: URL, base: URL): boolean {
    if (resolved.protocol !== base.protocol) {
        return false;
    }
    if (resolved.port !== base.port) {
        return false;
    }
    return normalizeHostname(resolved.hostname) === normalizeHostname(base.hostname);
}

/**
 * Validates the post-login `to` query value for same-origin navigation only.
 * Returns pathname + search + hash safe to pass to location.assign/replace/href,
 * or null when the value must be rejected (empty, parse error, cross-origin, non-http(s)).
 */
export function getSafePostLoginRedirect(
    rawTo: string | null | undefined,
    currentLocation?: string | URL
): string | null {
    if (rawTo == null) {
        return null;
    }
    const trimmed = rawTo.trim();
    if (trimmed.length === 0) {
        return null;
    }

    let baseHref: string;
    if (currentLocation !== undefined) {
        baseHref = typeof currentLocation === 'string' ? currentLocation : currentLocation.href;
    } else if (typeof window !== 'undefined' && window.location?.href) {
        baseHref = window.location.href;
    } else {
        return null;
    }

    let base: URL;
    let resolved: URL;
    try {
        base = new URL(baseHref);
        resolved = new URL(trimmed, base);
    } catch {
        return null;
    }

    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
        return null;
    }

    if (!isSameSiteForPostLoginRedirect(resolved, base)) {
        return null;
    }

    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}
